import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { useContext, useEffect, useRef, useState, type FormEvent } from "react";
import { PageContextValue, SiteContextValue, type CMSComponentProps } from "../engine/CMSComponent.tsx";
import RichText from "../engine/RichText.tsx";
import { isRichText, str } from "../types.ts";
import { Reveal, SectionLabel, SectionShell } from "./Sections.tsx";
import { linkProps, socialIcons } from "./SiteChrome.tsx";
import { Button } from "./ui/button.tsx";
import { Input } from "./ui/input.tsx";

type Status = "idle" | "sending" | "sent" | "error";

interface Turnstile {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let turnstileLoader: Promise<Turnstile> | undefined;

/**
 * Loads Turnstile once, in explicit mode. Rendering the widget from an effect
 * (instead of the implicit .cf-turnstile scan) keeps its injected iframe from
 * breaking React hydration. A script created by our nonce'd bundle is allowed
 * by the CSP through 'strict-dynamic'.
 */
const loadTurnstile = () =>
  (turnstileLoader ??= new Promise<Turnstile>((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile missing"));
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(script);
  }));

const labelClass = "mb-1.5 block text-sm font-medium text-foreground";

export default function ContactForm({ node }: CMSComponentProps) {
  const { turnstileSiteKey } = useContext(PageContextValue);
  const { social } = useContext(SiteContextValue);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | undefined>(undefined);
  const token = useRef("");

  const sent = status === "sent";

  useEffect(() => {
    if (!turnstileSiteKey || sent || !widgetRef.current) return;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !widgetRef.current) return;
        widgetId.current = turnstile.render(widgetRef.current, {
          sitekey: turnstileSiteKey,
          theme: "dark",
          callback: (value: string) => (token.current = value),
          "expired-callback": () => (token.current = ""),
        });
      })
      .catch((loadError) => console.error("[contact]", loadError));
    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = undefined;
      token.current = "";
    };
  }, [turnstileSiteKey, sent]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const form = event.currentTarget;
    const field = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement).value;

    if (turnstileSiteKey && !token.current) {
      setError("Please complete the verification and try again.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: field("name"),
          email: field("email"),
          message: field("message"),
          company: field("company"),
          turnstileToken: token.current,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Something went wrong");
      }
      setStatus("sent");
      form.reset();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Something went wrong");
      setStatus("error");
      // Turnstile tokens are single use; reset so a retry gets a fresh one
      token.current = "";
      window.turnstile?.reset(widgetId.current);
    }
  }

  const sending = status === "sending";

  return (
    <SectionShell node={node}>
      <Reveal className="space-y-4">
        <SectionLabel>{str(node.title)}</SectionLabel>
        {isRichText(node.intro) && (
          <div className="text-[15px] leading-relaxed text-foreground/80">
            <RichText document={node.intro} />
          </div>
        )}
      </Reveal>

      <Reveal className="mt-6">
        {sent ? (
          <div className="rounded-lg border border-border bg-card p-5" role="status">
            <p className="font-medium text-foreground">{str(node.successTitle)}</p>
            <p className="mt-1 text-sm text-muted-foreground">{str(node.successMessage)}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* honeypot: hidden from people, filled by bots */}
            <div aria-hidden="true" className="sr-only">
              <label htmlFor="company">Company (leave blank)</label>
              <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={labelClass}>
                  Name
                </label>
                <Input id="name" name="name" type="text" required maxLength={100} disabled={sending} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  Email
                </label>
                <Input id="email" name="email" type="email" required maxLength={200} disabled={sending} />
              </div>
            </div>

            <div>
              <label htmlFor="message" className={labelClass}>
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={5000}
                rows={5}
                disabled={sending}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {turnstileSiteKey && <div ref={widgetRef} />}

            {status === "error" && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" size="lg" disabled={sending}>
              {sending ? "Sending..." : (str(node.submitLabel) ?? "Send message")}
            </Button>
          </form>
        )}
      </Reveal>

      {(social.email || social.links.length > 0) && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-8 flex flex-wrap items-center gap-5 border-t border-border pt-6"
        >
          {social.email && (
            <a
              href={`mailto:${social.email}`}
              className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <Mail className="h-4 w-4" />
              {social.email}
            </a>
          )}
          {social.links.map((link) => {
            const Icon = socialIcons[link.icon];
            return (
              <a
                key={link.href}
                {...linkProps(link.href)}
                className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <Icon className="h-4 w-4" />
                {link.label}
              </a>
            );
          })}
        </motion.div>
      )}
    </SectionShell>
  );
}
